import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { ArrowLeft, FileSpreadsheet, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { getInstamojoSession, clearInstamojoSession } from './InstamojoPasswordGate';

// Trigger a browser download from a Blob with a server-provided filename
// (or a fallback name if the server didn't include Content-Disposition).
function downloadBlob(blob, fallbackName, headers) {
  const cd = headers?.['content-disposition'] || '';
  const m = cd.match(/filename\*?=(?:UTF-8'')?\"?([^\";]+)/i);
  const filename = (m && decodeURIComponent(m[1])) || fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 200);
}

export default function InstamojoReports() {
  const navigate = useNavigate();

  const [titleId, setTitleId]     = useState(0);
  const [titleText, setTitleText] = useState('');
  const [paidPwd, setPaidPwd]     = useState('');
  const [paidWithDatePwd, setPaidWithDatePwd] = useState('');

  // Autocomplete
  const [acOpen, setAcOpen]   = useState(false);
  const [acItems, setAcItems] = useState([]);
  const acTimer = useRef(null);

  // Per-tile loading flags
  const [downloading, setDownloading] = useState('');

  useEffect(() => {
    if (!getInstamojoSession()) navigate('/instamojo', { replace: true });
  }, [navigate]);

  // ── Purpose autocomplete ─────────────────────────────────────────────────
  const onTitleChange = (e) => {
    const v = e.target.value;
    setTitleText(v);
    setTitleId(0);
    if (acTimer.current) clearTimeout(acTimer.current);
    if (v.trim().length < 3) { setAcOpen(false); setAcItems([]); return; }
    acTimer.current = setTimeout(async () => {
      try {
        const res = await api.get('/instamojo-archive/purposes/autocomplete', {
          params: { q: v.trim(), limit: 10 },
        });
        setAcItems(res.data || []);
        setAcOpen(true);
      } catch {/* ignore */}
    }, 250);
  };
  const pickTitle = (item) => {
    setTitleText(item.title || '');
    setTitleId(item.id);
    setAcOpen(false);
  };
  const clearTitle = () => {
    setTitleText('');
    setTitleId(0);
    setAcOpen(false);
  };

  // ── Downloads ────────────────────────────────────────────────────────────
  const baseParams = () => titleId ? { title_id: titleId } : {};

  const doGet = async (key, path, fallbackName) => {
    setDownloading(key);
    try {
      const res = await api.get(`/instamojo-archive/${path}`, {
        params: baseParams(),
        responseType: 'blob',
      });
      downloadBlob(res.data, fallbackName, res.headers);
    } catch (err) {
      if (err.response?.status === 403) {
        clearInstamojoSession();
        toast.error('Session expired.');
        navigate('/instamojo', { replace: true });
        return;
      }
      // Blob responses for errors: parse the JSON body if we can.
      let detail = 'Download failed.';
      try {
        const txt = await err.response?.data?.text?.();
        if (txt) detail = JSON.parse(txt)?.detail || detail;
      } catch {/* ignore */}
      toast.error(detail);
    } finally {
      setDownloading('');
    }
  };

  const doPaidToCustomer = async () => {
    if (!paidPwd.trim()) { toast.error('Enter password.'); return; }
    setDownloading('paid');
    try {
      const res = await api.post(
        '/instamojo-archive/reports/paid-to-customer',
        { password: paidPwd },
        { params: baseParams(), responseType: 'blob' },
      );
      downloadBlob(res.data, 'Instamojo_Paid_to_customer_Report.xlsx', res.headers);
      setPaidPwd('');
    } catch (err) {
      if (err.response?.status === 403) {
        clearInstamojoSession();
        toast.error('Session expired.');
        navigate('/instamojo', { replace: true });
        return;
      }
      let detail = 'Wrong password!!';
      try {
        const txt = await err.response?.data?.text?.();
        if (txt) detail = JSON.parse(txt)?.detail || detail;
      } catch {/* ignore */}
      toast.error(detail);
    } finally {
      setDownloading('');
    }
  };

  // "Paid to customer Report with transaction date" — same password as the
  // standard Paid report (matches Rotary: both use AppSettings.Paid_to_customer_Report_pass).
  const doPaidWithDate = async () => {
    if (!paidWithDatePwd.trim()) { toast.error('Enter password.'); return; }
    setDownloading('paid-with-date');
    try {
      const res = await api.post(
        '/instamojo-archive/reports/paid-with-date',
        { password: paidWithDatePwd },
        { params: baseParams(), responseType: 'blob' },
      );
      downloadBlob(res.data, 'Instamojo_Paid_to_customer_Report_with_transaction_date.xlsx', res.headers);
      setPaidWithDatePwd('');
    } catch (err) {
      if (err.response?.status === 403) {
        clearInstamojoSession();
        toast.error('Session expired.');
        navigate('/instamojo', { replace: true });
        return;
      }
      let detail = 'Wrong password!!';
      try {
        const txt = await err.response?.data?.text?.();
        if (txt) detail = JSON.parse(txt)?.detail || detail;
      } catch {/* ignore */}
      toast.error(detail);
    } finally {
      setDownloading('');
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const Tile = ({ id, label, onClick, disabled = false, children }) => (
    <Card className="p-5 flex flex-col items-center text-center gap-3 bg-white">
      <div className="text-sm font-semibold text-slate-700">{label}</div>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled || !!downloading}
        title={disabled ? `${label} — not implemented yet` : `Download ${label}`}
        className="rounded-full p-3 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed text-emerald-700 transition">
        {downloading === id
          ? <Loader2 className="w-6 h-6 animate-spin" />
          : <FileSpreadsheet className="w-6 h-6" />}
      </button>
      {children}
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-3 md:p-4">
      <div className="w-full max-w-6xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => navigate('/instamojo/list')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-bold text-slate-900">Instamojo Reports Page</h1>
          </div>
        </div>

        {/* Purpose filter */}
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2 relative">
              <Label>Purpose</Label>
              <div className="flex gap-2">
                <Input value={titleText}
                  onChange={onTitleChange}
                  onFocus={() => acItems.length && setAcOpen(true)}
                  onBlur={() => setTimeout(() => setAcOpen(false), 150)}
                  placeholder="Enter Purpose (min 3 chars; leave blank for all)" />
                {titleText && (
                  <Button variant="outline" size="icon" onClick={clearTitle} title="Clear">
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {acOpen && acItems.length > 0 && (
                <div className="absolute z-20 mt-1 w-[calc(100%-3rem)] bg-white border border-slate-200 rounded-md shadow max-h-64 overflow-y-auto">
                  {acItems.map((it) => (
                    <button key={it.id}
                      type="button"
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-violet-50"
                      onMouseDown={(e) => { e.preventDefault(); pickTitle(it); }}>
                      {it.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="text-sm text-slate-500 self-end pb-2">
              {titleId
                ? <>Filtering by <span className="font-medium">{titleText}</span></>
                : <>No Purpose selected — exports run for <span className="font-medium">all</span> purposes.</>}
            </div>
          </div>
        </Card>

        {/* Two cards: Instamojo Reports + Payout Reports */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-4">
            <h4 className="text-lg font-bold text-slate-900 mb-3">Instamojo Reports</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Tile
                id="customer"
                label="Instamojo Report Customer"
                onClick={() => doGet('customer', 'reports/instamojo-customer', 'InstamojoReport.xlsx')} />
              <Tile
                id="summary"
                label="Customer Payment Report"
                onClick={() => doGet('summary', 'reports/customer-payment', 'Instamojo_paymentSummary_details.xlsx')} />
            </div>
          </Card>

          <Card className="p-4">
            <h4 className="text-lg font-bold text-slate-900 mb-3">Payout Reports</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Tile
                id="pending"
                label="Pending Customer Payout"
                onClick={() => doGet('pending', 'reports/pending-payout', 'Pendingpayout.xlsx')} />
              <Tile
                id="paid"
                label="Paid to customer Report"
                onClick={doPaidToCustomer}>
                <Input
                  type="password"
                  value={paidPwd}
                  onChange={(e) => setPaidPwd(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && doPaidToCustomer()}
                  placeholder="Password"
                  className="w-full" />
              </Tile>
              <Tile
                id="paid-with-date"
                label="Paid to customer Report with transaction date"
                onClick={doPaidWithDate}>
                <Input
                  type="password"
                  value={paidWithDatePwd}
                  onChange={(e) => setPaidWithDatePwd(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && doPaidWithDate()}
                  placeholder="Password"
                  className="w-full" />
              </Tile>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
